import React, { useCallback, useEffect, useState } from "react";
import { ADP_DATA, normalizeAdpName, getAdpRank } from "../../data/adpData";
import { POS_COLORS, UNIQUE_PLAYERS } from "../../data/players";
import LIVE_BOARD_KICKERS from "../../data/liveBoardKickers.json";
import { typeScale } from "./BoardScale";
import theme from "../../data/theme";

/**
 * PlayersLeft — the pool, crossed off. Four columns, taken players struck
 * through in place.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THIS IS A BOARD, NOT AN ASSISTANT — read WatchPage.jsx's header before
 * adding anything here. Three things follow from it, and none of them are
 * negotiable:
 *
 *   1. NO PRICE, NO ESTIMATE, NO TIER. Nothing in this file may import
 *      dynamicPrices, tiers.js, displayTiers, TierBuilder or CheatSheetView.
 *      watchPage.test.js asserts that structurally AND asserts that the
 *      rendered output carries no "$" — source guards catch renames, render
 *      guards catch the ways around them (multi-line imports, React.lazy,
 *      require).
 *
 *   2. 🔴 NEVER posRank, getAdpPosRank OR getEffectivePosRank. adpData.js
 *      REWRITES `_posRankMap` at module load from board-overrides.json —
 *      Mock 1's own editorial rank layer, which is exactly the opinion rule 1
 *      keeps off this page. Ordering or labelling with it would publish our
 *      board to the eleven people the publisher is bidding against. The raw
 *      `adpRank` field is the ingest order and carries no override; it is what
 *      orders this list, and NO RANK NUMBER IS PRINTED AT ALL. Name and NFL
 *      team, nothing else.
 *
 *      That is why the ordering test mutates a board-overrides.json entry and
 *      asserts this list does not move. It is the assertion worth the most in
 *      the file.
 *
 *   3. THE SOURCE IS PUBLIC. Underdog ADP, Aug 14-16 2026, already shipped in
 *      the app. A spectator could pull the same list up on their own phone;
 *      that is the standing test for what belongs on this page.
 * ══════════════════════════════════════════════════════════════════════
 *
 * 🔴 IT REPLACES THE ROSTER GRID, IT DOES NOT SIT ABOVE IT. The tab row is
 * already the most expensive thing /watch added above the grid, and the TV
 * budget is ~858px for a 1080p cast. A third view that costs no vertical space
 * cannot regress that fit work; one that stacked would.
 */

/**
 * How deep each column runs.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 A COLUMN THAT IS ENTIRELY STRUCK THROUGH ANSWERS NO QUESTION. The tab
 * exists to say who is STILL AVAILABLE, and it has to keep saying it at the
 * back half of the draft, which is exactly when the question gets asked.
 *
 * The first cut multiplied the team count by 2 (QB, TE) and 3 (RB, WR). Those
 * multipliers were specced from intuition and they are measurably too shallow.
 * Replayed against the real 2025 Smithtown auction, at a full board:
 *
 *     pos   depth   struck   untaken
 *     WR       30       28         2      <- the reported defect
 *     RB       30       26         4
 *     QB       20       13         7
 *     TE       20        9        11
 *
 * ── THE BASIS ────────────────────────────────────────────────────────
 * `DRAFTED_SHARE` is what each position ACTUALLY takes of a draft, pooled over
 * 640 picks — all four Smithtown auctions, 2022-2025, 10 teams x 16 slots — in
 * `docs/data/strong_island_fantasy_almanac_2009-2025.json` (`stats.drafts`,
 * the four rows with `draftType: "auction"`). Per season the counts are
 * WR 61/58/61/57, RB 48/50/51/50, QB 18/17/20/18, TE 13/12/17/13; the shares
 * are stable to within 2pp across the four, so this is a basis rather than one
 * league-year's noise. DEF 6.7% and K 4.8% are measured the same way and are
 * recorded here so the next person does not have to re-derive them.
 *
 * 🔴 CITED RATHER THAN RE-GUESSED. The file, the pick count and the seasons are
 * named above deliberately — the numbers this replaces were invented, and the
 * way that happens again is somebody needing the basis and not finding it.
 *
 * ── THE FORMULA ──────────────────────────────────────────────────────
 *   depth = ceil(share x teams x rosterSize) + teams x 2
 *
 * Expected consumption, plus roughly two more rounds of alternatives still
 * visible at every position when the last pick lands. It reads the board's own
 * size, so a 12-team or a 20-slot league scales instead of inheriting numbers
 * that happened to suit one shape. Ten teams x sixteen gives 39/70/80/34.
 * ══════════════════════════════════════════════════════════════════════
 */
export const DRAFTED_SHARE = { QB: 0.114, RB: 0.311, WR: 0.370, TE: 0.086 };

/**
 * ⚠️ SUPERFLEX DOUBLES QB DEMAND, so the 1QB share would run a QB column dry.
 * 0.28 is measured on the Sleeper superflex corpus (`docs/data/
 * sleeper-pricing-read-aug13.md`). One condition, keyed on the SFLEX slot
 * itself — the same literal test `is_sflex` uses, not a format label.
 */
export const SFLEX_QB_SHARE = 0.28;

export const POOL_COLUMNS = ["QB", "RB", "WR", "TE"];

/**
 * ── DEF AND K ────────────────────────────────────────────────────────
 *
 * ~11 defenses and ~8 kickers go every year (6.7% and 4.8% of 640 picks), so
 * "every position" means them too. They were missing because ADP_DATA holds no
 * DEF or K rows at all — a data gap, not a decision.
 *
 * Both columns are now the WHOLE pool: 32 defenses from UNIQUE_PLAYERS, and 32
 * kickers from liveBoardKickers.json — see the KICKER_POOL header for why the
 * kickers needed a file of their own.
 *
 * 🔴 ALPHABETICAL, AND THAT IS THE WHOLE POINT. There is no public ADP to order
 * these by, so ANY meaningful order would be OURS — which is exactly what trap
 * 2 forbids. Alphabetical is the one ordering that says nothing about who is
 * good. `posRank` is on these rows (`DEF1`, `K3`) and is never read.
 *
 * ⚠️ PROJECTED TO name/pos/team AT THE SOURCE. UNIQUE_PLAYERS rows carry
 * `value` and `tier` — Mock 1's own read on the market — and the way that gets
 * rendered one day is somebody spreading the whole object into a row. The
 * fields simply do not survive the trip.
 *
 * 🔴 ONE MERGED COLUMN, NOT TWO. RosterBoard's TV lesson is that columns cost
 * width and width is what stops names ellipsising; the longest strings in the
 * app are defense names ("Los Angeles Chargers"), so a narrow sixth column
 * would mangle exactly the rows it added. Stacked, the two ~20-row lists come
 * to ~40 — well under WR's 80 — so the merge costs no page height either.
 */
export const POOL_GROUPS = [["QB"], ["RB"], ["WR"], ["TE"], ["DEF", "K"]];

const POOL_ONLY = new Set(["DEF", "K"]);

/**
 * 🔴 DEF AND K ARE NOT CAPPED. Every defense and every kicker in the pool,
 * alphabetically, however deep the draft goes.
 *
 * The cap was two per team — enough to cover the ~11 defenses and ~8 kickers
 * that actually go in a Smithtown year, which is the wrong question. This
 * column answers WHO IS LEFT, and at these positions the late rounds are
 * exactly when somebody asks: a 10-team board showed 20 of 32 defenses, so a
 * spectator scanning for one that is not on the list could not tell "gone"
 * from "never shown". A truncated list is a wrong answer, not a shorter one.
 *
 * ⚠️ IT COSTS NO WIDTH AND ALMOST NO HEIGHT. DEF and K share one grid cell and
 * stack (see POOL_GROUPS), so the cast is still five columns; 32 + 32 rows
 * comes to ~64 against WR's 80 in the same row of the grid, which already sets
 * the height.
 *
 * ⚠️ QB/RB/WR/TE DEPTH IS UNCHANGED — those columns are cut from a
 * six-hundred-row ADP table and DRAFTED_SHARE is what keeps them readable.
 * This is only about the two positions whose entire pool is ~32 names.
 */

/** Slots per team, defaulting to the calibrated 16 rather than to zero. */
function rosterSizeOf(board) {
  const n = Number(board?.rosterSize);
  return Number.isFinite(n) && n > 0 ? n : 16;
}

export function teamCountOf(board) {
  return Math.max(1, (board?.teamNames || []).length);
}

/**
 * How many names this column should carry for THIS board.
 *
 * ⚠️ UNCAPPED HERE — `poolFor` caps against what ADP_DATA actually holds (181
 * QB, 355 RB, 604 WR, 306 TE). No league shape reaches those at these shares,
 * but a very large room must not index off the end of the table.
 */
export function poolDepth(pos, board) {
  const teams = teamCountOf(board);
  // Uncapped: `poolFor` slices to this, and the DEF/K pools are ~32 long.
  if (POOL_ONLY.has(pos)) return Infinity;
  const totalSlots = teams * rosterSizeOf(board);
  const sflex = Number(board?.positions?.SFLEX) > 0;
  const share = (pos === "QB" && sflex) ? SFLEX_QB_SHARE : (DRAFTED_SHARE[pos] || 0.1);
  return Math.ceil(share * totalSlots) + teams * 2;
}

/**
 * ── HIDE TAKEN ──────────────────────────────────────────────────────
 *
 * 🔴 DEFAULT OFF, BECAUSE THE CROSSING-OFF IS THE POINT. A pool that silently
 * drops the names it has taken looks like a shorter pool, not a draft in
 * progress — the struck-through rows are the whole reason to open this tab
 * rather than read a list. Hiding them is for the person who has stopped
 * caring who went and wants the shortlist; that is a choice, not a default.
 *
 * PER DEVICE, NEVER PUBLISHED — the same rule as the scale and the clips
 * toggle, for the same reason. A TV across the room and a phone in a hand want
 * different answers, and a display preference has no business in board state.
 * Wrapped because localStorage throws outright in some locked-down browsers,
 * and in that case the answer is the default rather than a broken page.
 */
export const HIDE_TAKEN_KEY = "mock1_watch_hide_taken_v1";

export function readHideTaken() {
  try {
    return window.localStorage.getItem(HIDE_TAKEN_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeHideTaken(on) {
  try {
    if (on) window.localStorage.setItem(HIDE_TAKEN_KEY, "1");
    else window.localStorage.removeItem(HIDE_TAKEN_KEY);
    return true;
  } catch {
    return false;                     // applies this session, does not persist
  }
}

export function useHideTaken() {
  const [on, setOn] = useState(() => readHideTaken());
  const choose = useCallback((next) => {
    setOn(next);
    writeHideTaken(next);
  }, []);
  return [on, choose];
}

export function HideTakenToggle({ on, onChange }) {
  return (
    <button
      data-testid="hide-taken-toggle"
      onClick={() => onChange(!on)}
      aria-pressed={on}
      title={on ? "Show the players already taken, struck through" : "Hide the players already taken"}
      style={{
        background: on ? theme.colors.brand.orange : "transparent",
        border: `1px solid ${on ? theme.colors.brand.orange : theme.colors.border.default}`,
        borderRadius: theme.radius.sm,
        color: on ? "#fff" : theme.colors.text.muted,
        fontFamily: "inherit", fontSize: theme.fontSize.micro,
        padding: "2px 9px", cursor: "pointer",
        textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: theme.fontWeight.bold,
      }}
    >Hide taken</button>
  );
}

/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE K COLUMN IS THE 32 STARTING KICKERS, NOT THE 18 IN players.js.
 *
 * Every NFL team has exactly one kicker and one of them goes in almost every
 * auction, so "who is left at K" has a 32-row answer. players.js carries 18 —
 * it is the DRAFTABLE pool, sized for a mock draft where nobody is scrolling
 * for the Browns' kicker, and padding it would change what the engine, the
 * tier maps and every price probe see. So the missing names live here instead,
 * in their own file, read by this surface alone.
 *
 * `src/data/liveBoardKickers.json` is the Sleeper player index filtered to
 * `position K · status Active · a team · depth_chart_order 1`, fetched by hand
 * with the date in the file. Thirty-two rows, one per team, no duplicates.
 *
 * 🔴 players.js SPELLING WINS. Seventeen of the eighteen match by
 * `normalizeAdpName`, and those render and join under the players.js name, so
 * a sale typed off the autocomplete strikes its row exactly as commit 4 made
 * it. The other fifteen render under the Sleeper name and cross off when sold
 * under it — the same normalise join, one namespace, no fuzzy matching.
 *
 * 🔴 IT IS THE UNION, SO 33 AND NOT 32 — owner's call, reversing the first
 * cut. Sleeper lists one starter per team, and players.js carries one kicker
 * Sleeper does not (Zane Gonzalez, ATL; Nick Folk has that job now). Leaving
 * him out meant a sale for a name the autocomplete still offers struck no row
 * at all, which is the silent miss this whole column was rebuilt to remove.
 * A backup on the list costs one row; a sold player missing from it costs the
 * answer.
 * ══════════════════════════════════════════════════════════════════════
 */
const KICKER_POOL = (() => {
  const ours = new Map();
  for (const p of UNIQUE_PLAYERS) {
    if (p.pos === "K") ours.set(normalizeAdpName(p.name), p);
  }
  const seen = new Set();
  const out = [];
  for (const k of (LIVE_BOARD_KICKERS.kickers || [])) {
    const key = normalizeAdpName(k.name);
    seen.add(key);
    const mine = ours.get(key);
    // Projected to name/pos/team either way — see the POOL_GROUPS header on why
    // a UNIQUE_PLAYERS row is never spread.
    out.push(mine
      ? { name: mine.name, pos: "K", team: mine.team || k.team }
      : { name: k.name, pos: "K", team: k.team });
  }
  // Then whoever players.js has that Sleeper's starter list does not.
  for (const [key, p] of ours) {
    if (!seen.has(key)) out.push({ name: p.name, pos: "K", team: p.team });
  }
  return out;
})();

/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE POOL AND THE SALES MUST BE IN ONE NAMESPACE, OR THE CROSSING-OFF
 * SILENTLY MISSES.
 *
 * THE BUG, observed Aug 22 on a real board: Kenneth Gainwell sold and stayed
 * unstruck all night. The pool is built from ADP_DATA, which carries Underdog
 * spellings ("Kenny Gainwell"); a sale carries the players.js spelling,
 * because `saleFromPlayer` writes `player.name` verbatim off the autocomplete.
 * `normalizeAdpName` handles punctuation and suffixes — "Patrick Mahomes II"
 * vs "Patrick Mahomes" — but it is a string normaliser, not a nickname map, so
 * Kenny/Kenneth is two different keys and the join simply misses. An unstruck
 * row looks exactly like a player nobody has taken, which is the one thing
 * this tab exists to answer.
 *
 * THE FIX: resolve every ADP row to its players.js entry when the pool is
 * built, and display and join on the players.js name. adpData.js's own
 * NAME_ALIASES layer already binds the two spellings — it aliases the ADP
 * MAPS, so `getAdpRank("Kenneth Gainwell")` returns Kenny Gainwell's rank —
 * so the resolution is a lookup through the shipped rule rather than a second
 * copy of it.
 *
 * TWO STEPS, IN THIS ORDER:
 *   1. same normalised spelling at the same position — the ordinary case, and
 *      it must win, so an exact match is never displaced by an alias;
 *   2. same position and same adpRank — the alias case, and the only thing
 *      that reaches it is a row whose spelling players.js does not carry.
 *
 * ⚠️ THE FALLBACK IS THE ADP NAME, AND IT IS NOT FUZZY. A row that resolves to
 * nothing renders and joins on its own spelling, exactly as before. Guessing
 * would be worse than missing: crossing off the wrong player is silent.
 * ══════════════════════════════════════════════════════════════════════
 */
const POOL_NAME_BY_SPELLING = new Map();   // `${pos}|${normalised}` -> players.js name
const POOL_NAME_BY_ADP_RANK = new Map();   // `${pos}|${adpRank}`    -> players.js name
for (const p of UNIQUE_PLAYERS) {
  const spelling = `${p.pos}|${normalizeAdpName(p.name)}`;
  if (!POOL_NAME_BY_SPELLING.has(spelling)) POOL_NAME_BY_SPELLING.set(spelling, p.name);
  const rank = getAdpRank(p.name);
  if (rank > 0 && rank < 999) {
    const byRank = `${p.pos}|${rank}`;
    if (!POOL_NAME_BY_ADP_RANK.has(byRank)) POOL_NAME_BY_ADP_RANK.set(byRank, p.name);
  }
}

/**
 * One ADP row's name in the players.js namespace, or its own name when
 * players.js does not carry him at all.
 *
 * Exported so a test can count the rows that do not resolve. That count is a
 * DATA fact, not a display one — a name in the ADP export that the app's own
 * pool has never heard of.
 */
export function poolNameFor(row) {
  if (!row?.name) return "";
  return POOL_NAME_BY_SPELLING.get(`${row.pos}|${normalizeAdpName(row.name)}`)
    || POOL_NAME_BY_ADP_RANK.get(`${row.pos}|${row.adpRank}`)
    || row.name;
}

/** True when this ADP row has a players.js entry under either rule. */
export function resolvesToPool(row) {
  if (!row?.name) return false;
  return POOL_NAME_BY_SPELLING.has(`${row.pos}|${normalizeAdpName(row.name)}`)
    || POOL_NAME_BY_ADP_RANK.has(`${row.pos}|${row.adpRank}`);
}

/**
 * ── PHONE PORTRAIT ───────────────────────────────────────────────────
 *
 * 🔴 FIVE COLUMNS SIDE BY SIDE ON A PORTRAIT PHONE LEAVE EACH NAME ~70px, so
 * people rotated the handset to read the tab. Below 640px this renders ONE
 * position group at a time, full width, with a tab row above it.
 *
 * ⚠️ 640px AND NOT THE APP'S USUAL 820. This is not "is this a mobile
 * layout" — it is "does a name fit". A 768px tablet in portrait gives each of
 * five columns ~145px, which reads; a 390px phone gives ~70px, which does not.
 * The app's 820 breakpoint answers a different question and borrowing it would
 * put a tablet into a one-column layout it does not need.
 *
 * 🔴 AT AND ABOVE 640px NOTHING MOVES, PIXEL FOR PIXEL. The five-column grid,
 * `typeScale`, `BoardScale` and every width constant they read are untouched —
 * this adds a branch above them, it does not reach into them. The TV cast runs
 * far above this width and cannot see any of it.
 *
 * ⚠️ THE CHOSEN TAB IS PER DEVICE, exactly like HIDE_TAKEN_KEY and the scale,
 * and for the same reason: it is a display preference and has no business in
 * board state. Its own key, beside that one.
 *
 * DEFAULTS TO RB — the deepest column anyone actually shops in mid-draft, and
 * the position the reported defect was about.
 */
export const NARROW_MAX_PX = 639;
export const POOL_TAB_KEY = "mock1_watch_pool_tab_v1";
export const DEFAULT_POOL_TAB = "RB";

/** A group's key and its tab label — DEF and K share a cell, so "DEF/K". */
export const poolGroupKey = (group) => group.join("");
export const poolGroupLabel = (group) => group.join("/");

export function readPoolTab() {
  try {
    const v = window.localStorage.getItem(POOL_TAB_KEY);
    return POOL_GROUPS.some(g => poolGroupKey(g) === v) ? v : DEFAULT_POOL_TAB;
  } catch {
    return DEFAULT_POOL_TAB;
  }
}

export function writePoolTab(key) {
  try {
    window.localStorage.setItem(POOL_TAB_KEY, key);
    return true;
  } catch {
    return false;                     // applies this session, does not persist
  }
}

/**
 * Whether this viewport is narrow enough to need one column at a time.
 *
 * Same shape as LandingPage/AboutPage's own check — `innerWidth` plus a resize
 * listener — rather than a media query, because a rotation has to move it and
 * the value has to be readable synchronously on the first render.
 */
export function useNarrowPool() {
  const [narrow, setNarrow] = useState(() => {
    try { return window.innerWidth <= NARROW_MAX_PX; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => {
      try { setNarrow(window.innerWidth <= NARROW_MAX_PX); } catch { /* ignore */ }
    };
    window.addEventListener("resize", onResize);
    onResize();                       // a rotation between mount and effect
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return narrow;
}

/**
 * The pool for one position: the first `depth` names by RAW adpRank.
 *
 * ⚠️ Sorted here rather than trusted: ADP_DATA is authored in rank order today,
 * and a re-ingest that landed one row out of place would otherwise reorder a
 * column silently. Sorting on the same field the test pins makes that a no-op.
 *
 * ⚠️ THE NAME IS RESOLVED, THE ORDER AND THE TEAM ARE NOT. `adpRank` still
 * orders the column and the ADP export still supplies the NFL team — it is the
 * fresher of the two sources and the resolution is about the JOIN, not about
 * replacing the row.
 */
export function poolFor(pos, board) {
  const depth = poolDepth(pos, board);
  if (POOL_ONLY.has(pos)) {
    // K comes from the 32-row kicker file (see KICKER_POOL); DEF from
    // UNIQUE_PLAYERS, which already carries all 32.
    const rows = pos === "K"
      ? KICKER_POOL
      // Projected, not spread — see the POOL_GROUPS header. `value` and `tier`
      // ride along on these rows and must not reach a spectator's screen.
      : UNIQUE_PLAYERS.filter(p => p.pos === pos).map(p => ({ name: p.name, pos: p.pos, team: p.team }));
    return rows
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, depth);
  }
  return ADP_DATA
    .filter(p => p.pos === pos)
    .slice()
    .sort((a, b) => a.adpRank - b.adpRank)
    .slice(0, depth)
    .map(p => ({ ...p, name: poolNameFor(p) }));
}

/**
 * 🔴 THE JOIN GOES THROUGH normalizeAdpName, ALWAYS. `saleFromPlayer` writes
 * `player.name` verbatim, so a sale reads "Patrick Mahomes II" while ADP_DATA
 * reads "Patrick Mahomes" — a raw compare leaves him uncrossed all night and
 * looks exactly like a player nobody has taken.
 *
 * FREE-TEXT SALES MATCH NOTHING, AND THAT IS CORRECT. Somebody typed a name
 * this table does not carry; the draft log still shows the sale. Nothing here
 * fuzzy-matches — crossing off the wrong player is worse than crossing off
 * none, because it is silent.
 */
export function takenNames(board) {
  const out = new Set();
  for (const sale of board?.sales || []) {
    if (sale?.name) out.add(normalizeAdpName(sale.name));
  }
  return out;
}

export default function PlayersLeft({ board, scale = 1, hideTaken = false }) {
  const taken = takenNames(board);
  const type = typeScale(scale);
  const s = (n) => Math.round(n * type);

  // See the PHONE PORTRAIT header. Both are inert at and above 640px: `narrow`
  // is false, so the grid below renders exactly what it always did.
  const narrow = useNarrowPool();
  const [tab, setTab] = useState(() => readPoolTab());
  const chooseTab = useCallback((key) => { setTab(key); writePoolTab(key); }, []);
  const groups = narrow
    ? POOL_GROUPS.filter(g => poolGroupKey(g) === tab)
    : POOL_GROUPS;

  return (
    <div data-testid="players-left">
      {narrow && (
        <div
          data-testid="pool-tabs"
          role="tablist"
          style={{ display: "flex", gap: s(4), marginBottom: s(8), flexWrap: "wrap" }}
        >
          {POOL_GROUPS.map(group => {
            const key = poolGroupKey(group);
            const on = key === tab;
            return (
              <button
                key={key}
                role="tab"
                aria-selected={on}
                data-testid={`pool-tab-${key}`}
                onClick={() => chooseTab(key)}
                style={{
                  flex: 1, minWidth: 0,
                  background: on ? theme.colors.brand.orange : "transparent",
                  border: `1px solid ${on ? theme.colors.brand.orange : theme.colors.border.default}`,
                  borderRadius: theme.radius.sm,
                  color: on ? "#fff" : theme.colors.text.muted,
                  fontFamily: "inherit", fontSize: s(10),
                  fontWeight: theme.fontWeight.bold, letterSpacing: "0.06em",
                  padding: `${s(5)}px ${s(4)}px`, cursor: "pointer",
                }}
              >{poolGroupLabel(group)}</button>
            );
          })}
        </div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${groups.length}, minmax(0, 1fr))`,
          gap: s(10),
        }}
      >
        {groups.map(group => (
          /* One grid cell per GROUP. Every group but the last holds a single
             position, so their output is unchanged; DEF and K share a cell and
             stack, which is what keeps the cast at five columns. */
          <div key={group.join("-")} data-testid={`pool-group-${group.join("")}`} style={{ minWidth: 0 }}>
            {group.map((pos, i) => {
          const pool = poolFor(pos, board);
          const left = pool.filter(p => !taken.has(normalizeAdpName(p.name)));
          const rows = hideTaken ? left : pool;
          return (
            <div key={pos} data-testid={`pool-col-${pos}`} style={{ minWidth: 0, marginTop: i > 0 ? s(12) : 0 }}>
              <div style={{
                display: "flex", alignItems: "center", gap: s(6),
                marginBottom: s(6), paddingBottom: s(4),
                borderBottom: `1px solid ${theme.colors.border.default}`,
              }}>
                <span className="tag" style={{
                  background: POS_COLORS[pos]?.bg || theme.colors.bg.elevated,
                  color: POS_COLORS[pos]?.text || theme.colors.text.muted,
                  fontSize: s(9), padding: `${s(1)}px ${s(5)}px`,
                  fontWeight: theme.fontWeight.bold, textAlign: "center",
                }}>{pos}</span>
                {/* A count of what is left. Arithmetic anyone at the table
                    could do by looking at the column — the same standing test
                    the draft log's header passes. */}
                <span style={{ fontSize: s(9), color: theme.colors.text.faint, fontVariantNumeric: "tabular-nums" }}>
                  {left.length} left
                </span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: s(2) }}>
                {rows.map(p => {
                  const isTaken = taken.has(normalizeAdpName(p.name));
                  return (
                    <div
                      key={`${p.pos}-${p.name}`}
                      data-testid={`pool-row-${p.name}`}
                      data-taken={isTaken ? "1" : undefined}
                      style={{
                        display: "flex", alignItems: "baseline", gap: s(5),
                        opacity: isTaken ? 0.32 : 1,
                      }}
                    >
                      <span style={{
                        flex: 1, minWidth: 0,
                        fontSize: s(10),
                        fontWeight: isTaken ? 400 : 600,
                        color: isTaken ? theme.colors.text.muted : theme.colors.text.primary,
                        textDecoration: isTaken ? "line-through" : undefined,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>{p.name}</span>
                      <span style={{ fontSize: s(8), color: theme.colors.text.faint }}>{p.team}</span>
                    </div>
                  );
                })}
                {rows.length === 0 && (
                  <span style={{ fontSize: s(9), color: theme.colors.text.faint, fontStyle: "italic" }}>
                    All gone
                  </span>
                )}
              </div>
            </div>
          );
            })}
          </div>
        ))}
      </div>

      {/* Where these names come from, so nobody reads the ordering as a
          ranking. DEF and K have no public ADP at all, hence alphabetical. */}
      <div style={{ marginTop: s(10), fontSize: s(8), color: theme.colors.text.faint }}>
        Skill positions in ADP order. Defenses and kickers alphabetically, because they have no public ADP.
      </div>
    </div>
  );
}
